import { useEffect, useRef, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { apiUrl } from '../lib/api';
import { useAdminUnsaved } from '../contexts/AdminUnsavedContext';
import { useAdminDialog } from '../contexts/AdminDialogContext';
import {
  getCupons,
  getCupomById,
  addCupom,
  saveCupom,
  removeCupom,
  type Cupom,
  type TipoDescontoCupom,
  type EscopoCupom,
} from '../lib/cuponsStorage';
import { getEstabelecimentos } from '../lib/estabelecimentosStorage';
import { getExperiencias } from '../lib/experienciasStorage';
import { QRCodeSVG } from 'qrcode.react';

type ViewMode = 'list' | 'form';

const defaultForm: Partial<Cupom> = {
  codigo: '',
  descricao: '',
  tipoDesconto: 'percentual',
  valor: 0,
  escopo: 'plataforma',
  estabelecimentoId: '',
  experienciaIds: [],
  dataInicio: new Date().toISOString().slice(0, 10),
  dataFim: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  usosMaximos: 0,
  valorMinimoPedido: undefined,
  ativo: true,
};

function formatDateBR(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

export default function CuponsPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [cupons, setCupons] = useState<Cupom[]>([]);
  const [form, setForm] = useState<Partial<Cupom>>({ ...defaultForm });
  const [query, setQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const isFirstFormRenderRef = useRef(true);
  const persistCupomDraftRef = useRef<(opts?: { keepOnForm?: boolean }) => boolean>(() => false);
  const saveCupomPublishedRef = useRef<() => Promise<boolean>>(async () => false);
  const autoDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    isDirty,
    setDirty,
    setIsPublishedLeave,
    setFlushDraftHandler,
    setSavePublishedHandler,
  } = useAdminUnsaved();
  const { showAlert, showConfirm } = useAdminDialog();

  const estabelecimentos = getEstabelecimentos();
  const experiencias = getExperiencias();

  const refresh = () => setCupons(getCupons());

  useEffect(() => {
    refresh();
  }, []);

  useRefetchWhenCatalogChanges(() => refresh());

  useEffect(() => {
    if (viewMode !== 'form') {
      isFirstFormRenderRef.current = true;
      return;
    }
    if (isFirstFormRenderRef.current) {
      isFirstFormRenderRef.current = false;
      return;
    }
    setDirty(true);
  }, [form, viewMode, setDirty]);

  const updateForm = (partial: Partial<Cupom>) => {
    setForm((prev) => ({ ...prev, ...partial }));
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...defaultForm });
    setDirty(false);
    isFirstFormRenderRef.current = true;
    setViewMode('form');
  };

  const openEdit = (id: string) => {
    const c = getCupomById(id);
    if (!c) return;
    setEditingId(id);
    setForm({ ...c });
    setDirty(false);
    isFirstFormRenderRef.current = true;
    setViewMode('form');
  };

  const handleCancel = async () => {
    if (isDirty) {
      const ok = await showConfirm('Ao sair, você perderá as informações não salvas. Deseja realmente sair?', {
        title: 'Alterações não salvas',
        confirmText: 'Sair',
        cancelText: 'Cancelar',
      });
      if (!ok) return;
    }
    setDirty(false);
    setViewMode('list');
    setEditingId(null);
  };

  const CUPOM_CODIGO_LENGTH = 8;

  const handleSave = async () => {
    const codigo = (form.codigo ?? '').trim().toUpperCase();
    if (!codigo) {
      await showAlert('Informe o código do cupom.');
      return;
    }
    if (codigo.length !== CUPOM_CODIGO_LENGTH) {
      await showAlert(`O código do cupom deve ter exatamente ${CUPOM_CODIGO_LENGTH} caracteres.`);
      return;
    }
    if (form.tipoDesconto === 'percentual' && (form.valor ?? 0) <= 0) {
      await showAlert('Informe a porcentagem de desconto.');
      return;
    }
    if (form.tipoDesconto === 'valor_fixo' && (form.valor ?? 0) <= 0) {
      await showAlert('Informe o valor fixo do desconto.');
      return;
    }
    if (!form.dataInicio || !form.dataFim) {
      await showAlert('Informe as datas de validade.');
      return;
    }
    if (form.dataFim < form.dataInicio) {
      await showAlert('A data de fim deve ser posterior à data de início.');
      return;
    }
    try {
      const payload = {
        codigo,
        descricao: form.descricao ?? '',
        tipoDesconto: (form.tipoDesconto ?? 'percentual') as TipoDescontoCupom,
        valor: form.valor ?? 0,
        escopo: (form.escopo ?? 'plataforma') as EscopoCupom,
        estabelecimentoId: form.escopo === 'estabelecimento' ? form.estabelecimentoId : undefined,
        experienciaIds: form.escopo === 'experiencia' ? (form.experienciaIds ?? []) : undefined,
        dataInicio: form.dataInicio!,
        dataFim: form.dataFim!,
        usosMaximos: form.usosMaximos ?? 0,
        valorMinimoPedido: form.valorMinimoPedido ?? undefined,
        ativo: form.ativo ?? true,
        rascunho: false,
      };
      if (editingId) {
        const curr = getCupomById(editingId);
        if (!curr) throw new Error('Cupom não encontrado.');
        saveCupom({ ...curr, ...payload });
        await showAlert('Cupom atualizado com sucesso.', 'success');
      } else {
        addCupom(payload);
        await showAlert('Cupom criado com sucesso.', 'success');
      }
      setDirty(false);
      refresh();
      setViewMode('list');
      setEditingId(null);
    } catch (e) {
      await showAlert(e instanceof Error ? e.message : 'Erro ao salvar cupom.');
    }
  };

  const persistCupomDraftSilent = (opts?: { keepOnForm?: boolean }): boolean => {
    const payload: Omit<Cupom, 'id' | 'usosAtuais' | 'createdAt' | 'updatedAt'> = {
      codigo: (form.codigo ?? '').trim().toUpperCase(),
      descricao: form.descricao ?? '',
      tipoDesconto: (form.tipoDesconto ?? 'percentual') as TipoDescontoCupom,
      valor: Math.max(1, form.valor ?? 1),
      escopo: (form.escopo ?? 'plataforma') as EscopoCupom,
      estabelecimentoId: form.escopo === 'estabelecimento' ? form.estabelecimentoId : undefined,
      experienciaIds: form.escopo === 'experiencia' ? (form.experienciaIds ?? []) : undefined,
      dataInicio: form.dataInicio ?? new Date().toISOString().slice(0, 10),
      dataFim: form.dataFim ?? new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10),
      usosMaximos: form.usosMaximos ?? 0,
      valorMinimoPedido: form.valorMinimoPedido ?? undefined,
      ativo: false,
      rascunho: true,
    };
    try {
      if (editingId) {
        const curr = getCupomById(editingId);
        if (!curr) return false;
        saveCupom({ ...curr, ...payload });
        const updated = getCupomById(editingId);
        if (updated?.codigo) setForm((f) => ({ ...f, codigo: updated.codigo }));
      } else {
        const added = addCupom(payload);
        setEditingId(added.id);
        setForm((f) => ({ ...f, codigo: added.codigo }));
      }
    } catch {
      return false;
    }
    setDirty(false);
    refresh();
    if (!opts?.keepOnForm) {
      setViewMode('list');
      setEditingId(null);
    }
    return true;
  };

  const savePublishedCupomForLeave = async (): Promise<boolean> => {
    const codigo = (form.codigo ?? '').trim().toUpperCase();
    if (!codigo) {
      await showAlert('Informe o código do cupom.');
      return false;
    }
    if (codigo.length !== CUPOM_CODIGO_LENGTH) {
      await showAlert(`O código do cupom deve ter exatamente ${CUPOM_CODIGO_LENGTH} caracteres.`);
      return false;
    }
    if (form.tipoDesconto === 'percentual' && (form.valor ?? 0) <= 0) {
      await showAlert('Informe a porcentagem de desconto.');
      return false;
    }
    if (form.tipoDesconto === 'valor_fixo' && (form.valor ?? 0) <= 0) {
      await showAlert('Informe o valor fixo do desconto.');
      return false;
    }
    if (!form.dataInicio || !form.dataFim) {
      await showAlert('Informe as datas de validade.');
      return false;
    }
    if (form.dataFim < form.dataInicio) {
      await showAlert('A data de fim deve ser posterior à data de início.');
      return false;
    }
    try {
      const payload = {
        codigo,
        descricao: form.descricao ?? '',
        tipoDesconto: (form.tipoDesconto ?? 'percentual') as TipoDescontoCupom,
        valor: form.valor ?? 0,
        escopo: (form.escopo ?? 'plataforma') as EscopoCupom,
        estabelecimentoId: form.escopo === 'estabelecimento' ? form.estabelecimentoId : undefined,
        experienciaIds: form.escopo === 'experiencia' ? (form.experienciaIds ?? []) : undefined,
        dataInicio: form.dataInicio!,
        dataFim: form.dataFim!,
        usosMaximos: form.usosMaximos ?? 0,
        valorMinimoPedido: form.valorMinimoPedido ?? undefined,
        ativo: form.ativo ?? true,
        rascunho: false,
      };
      if (editingId) {
        const curr = getCupomById(editingId);
        if (!curr) throw new Error('Cupom não encontrado.');
        saveCupom({ ...curr, ...payload });
      } else {
        addCupom(payload);
      }
      setDirty(false);
      refresh();
      setViewMode('list');
      setEditingId(null);
      return true;
    } catch (e) {
      await showAlert(e instanceof Error ? e.message : 'Erro ao salvar cupom.');
      return false;
    }
  };

  persistCupomDraftRef.current = persistCupomDraftSilent;
  saveCupomPublishedRef.current = savePublishedCupomForLeave;

  useEffect(() => {
    if (viewMode !== 'form') {
      setIsPublishedLeave(false);
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
      return;
    }
    const rascunho = editingId ? (getCupomById(editingId)?.rascunho ?? false) : true;
    setIsPublishedLeave(Boolean(isDirty && !rascunho));
    setFlushDraftHandler(
      rascunho && isDirty ? async () => persistCupomDraftRef.current({ keepOnForm: true }) : null
    );
    setSavePublishedHandler(
      !rascunho && isDirty ? async () => saveCupomPublishedRef.current() : null
    );
    return () => {
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
    };
  }, [viewMode, editingId, isDirty, setIsPublishedLeave, setFlushDraftHandler, setSavePublishedHandler]);

  useEffect(() => {
    if (viewMode !== 'form') {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    const rascunho = editingId ? (getCupomById(editingId)?.rascunho ?? false) : true;
    if (!rascunho) {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    if (isFirstFormRenderRef.current) return;
    if (!isDirty) return;
    if (autoDraftTimerRef.current) clearTimeout(autoDraftTimerRef.current);
    autoDraftTimerRef.current = setTimeout(() => {
      autoDraftTimerRef.current = null;
      void persistCupomDraftRef.current({ keepOnForm: true });
    }, 1200);
    return () => {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
    };
  }, [form, viewMode, editingId, isDirty]);

  const handleSyncApi = async () => {
    if (cupons.length === 0) {
      await showAlert('Não há cupons para sincronizar.');
      return;
    }
    setSyncing(true);
    try {
      const payload = cupons.map((c) => ({
        id: c.id,
        codigo: c.codigo,
        tipoDesconto: c.tipoDesconto,
        valor: c.valor,
        escopo: c.escopo,
        estabelecimentoId: c.estabelecimentoId,
        experienciaIds: c.experienciaIds,
        dataInicio: c.dataInicio,
        dataFim: c.dataFim,
        usosMaximos: c.usosMaximos,
        usosAtuais: c.usosAtuais,
        valorMinimoPedido: c.valorMinimoPedido,
        ativo: c.ativo,
      }));
      const res = await fetch(apiUrl('/api/admin/cupons/sync'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cupons: payload }),
      });
      if (res.ok) {
        await showAlert('Cupons sincronizados com a API.', 'success');
      } else {
        const text = await res.text();
        await showAlert(`Erro ao sincronizar: ${res.status}. ${text.substring(0, 100)}`);
      }
    } catch (e) {
      await showAlert(e instanceof Error ? e.message : 'Erro ao sincronizar. Verifique se a API está disponível.');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (c: Cupom) => {
    const ok = await showConfirm(`Excluir o cupom "${c.codigo}"?`, {
      title: 'Excluir cupom',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
    });
    if (!ok) return;
    removeCupom(c.id);
    refresh();
  };

  const filtered = cupons.filter(
    (c) =>
      !query ||
      c.codigo.toLowerCase().includes(query.toLowerCase()) ||
      (c.descricao ?? '').toLowerCase().includes(query.toLowerCase())
  );

  if (viewMode === 'form') {
    return (
      <AdminLayout>
        <div className="p-6 max-w-3xl">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {editingId ? 'Editar cupom' : 'Novo cupom'}
          </h1>

          <div className="space-y-6 bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
            {/* Código - exatamente 8 caracteres */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código * (8 caracteres)</label>
              <input
                type="text"
                maxLength={8}
                value={(form.codigo ?? '').slice(0, 8)}
                onChange={(e) => updateForm({ codigo: e.target.value.toUpperCase().slice(0, 8) })}
                placeholder="Ex: NAVI1000"
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none font-mono tracking-widest"
              />
              <p className="text-xs text-gray-500 mt-1">{(form.codigo ?? '').length}/8 caracteres</p>
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <input
                type="text"
                value={form.descricao ?? ''}
                onChange={(e) => updateForm({ descricao: e.target.value })}
                placeholder="Ex: Desconto de boas-vindas"
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
              />
            </div>

            {/* Tipo e valor */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de desconto</label>
                <select
                  value={form.tipoDesconto ?? 'percentual'}
                  onChange={(e) => updateForm({ tipoDesconto: e.target.value as TipoDescontoCupom })}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
                >
                  <option value="percentual">Percentual (%)</option>
                  <option value="valor_fixo">Valor fixo (R$)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {form.tipoDesconto === 'valor_fixo' ? 'Valor (R$)' : 'Percentual (%)'}
                </label>
                <input
                  type="number"
                  min={0}
                  max={form.tipoDesconto === 'percentual' ? 100 : undefined}
                  step={form.tipoDesconto === 'percentual' ? 1 : 0.01}
                  value={form.valor ?? ''}
                  onChange={(e) => updateForm({ valor: Number(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
                />
              </div>
            </div>

            {/* Escopo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Escopo</label>
              <select
                value={form.escopo ?? 'plataforma'}
                onChange={(e) => updateForm({ escopo: e.target.value as EscopoCupom })}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
              >
                <option value="plataforma">Plataforma (todos os pedidos)</option>
                <option value="estabelecimento">Estabelecimento específico</option>
                <option value="experiencia">Experiência(s) específica(s)</option>
              </select>
            </div>

            {form.escopo === 'estabelecimento' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estabelecimento</label>
                <select
                  value={form.estabelecimentoId ?? ''}
                  onChange={(e) => updateForm({ estabelecimentoId: e.target.value || undefined })}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
                >
                  <option value="">Selecione</option>
                  {estabelecimentos.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nome}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {form.escopo === 'experiencia' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Experiências</label>
                <select
                  multiple
                  value={form.experienciaIds ?? []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                    updateForm({ experienciaIds: selected });
                  }}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none min-h-[120px]"
                >
                  {experiencias.map((exp) => (
                    <option key={exp.id} value={exp.id}>
                      {exp.titulo}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Segure Ctrl para selecionar múltiplas</p>
              </div>
            )}

            {/* Datas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data início</label>
                <input
                  type="date"
                  value={form.dataInicio ?? ''}
                  onChange={(e) => updateForm({ dataInicio: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data fim</label>
                <input
                  type="date"
                  value={form.dataFim ?? ''}
                  onChange={(e) => updateForm({ dataFim: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
                />
              </div>
            </div>

            {/* Usos e valor mínimo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usos máximos</label>
                <input
                  type="number"
                  min={0}
                  value={form.usosMaximos ?? 0}
                  onChange={(e) => updateForm({ usosMaximos: Number(e.target.value) || 0 })}
                  placeholder="0 = ilimitado"
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor mínimo (R$)</label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.valorMinimoPedido ?? ''}
                  onChange={(e) => updateForm({ valorMinimoPedido: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="Opcional"
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
                />
              </div>
            </div>

            {/* Ativo */}
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={form.ativo ?? true}
                onChange={(e) => updateForm({ ativo: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-700">Cupom ativo</span>
            </label>

            {/* QR Code */}
            {form.codigo?.trim() && (
              <div className="pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">QR Code</label>
                <div className="inline-flex flex-col items-center p-4 bg-white rounded-xl border-2 border-gray-200">
                  <QRCodeSVG value={form.codigo.trim().toUpperCase()} size={160} level="M" />
                  <p className="mt-2 text-sm text-gray-600 font-mono">{form.codigo.trim().toUpperCase()}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Cupons</h1>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSyncApi}
              disabled={syncing || cupons.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {syncing ? (
                <>Sincronizando...</>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sincronizar
                </>
              )}
            </button>
            <button
              type="button"
              onClick={openNew}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo cupom
            </button>
          </div>
        </div>

        <div className="mb-4">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por código ou descrição..."
            className="w-full max-w-md px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              {query ? 'Nenhum cupom encontrado.' : 'Nenhum cupom cadastrado. Clique em "Novo cupom" para criar.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Código</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Desconto</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Escopo</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Validade</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Usos</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-gray-900">{c.codigo}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {c.tipoDesconto === 'percentual' ? `${c.valor}%` : `R$ ${c.valor.toLocaleString('pt-BR')}`}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {c.escopo === 'plataforma' && 'Plataforma'}
                        {c.escopo === 'estabelecimento' && (estabelecimentos.find((e) => e.id === c.estabelecimentoId)?.nome ?? '—')}
                        {c.escopo === 'experiencia' && `${(c.experienciaIds ?? []).length} exp.`}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDateBR(c.dataInicio)} – {formatDateBR(c.dataFim)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {c.usosMaximos > 0 ? `${c.usosAtuais}/${c.usosMaximos}` : '∞'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            c.ativo ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {c.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(c.id)}
                            className="p-2 rounded-lg text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(c)}
                            className="p-2 rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Excluir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
